import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { UrlBuilder } from '../../../core';
import { GridDetails, GridHeader } from '../../tracking-overview/interfaces/grid';
import { IBreadcrumb, BreadcrumbService } from '../../shared/breadcrumb-service/breadcrumb.service';
import { SamplesHelperService } from '../../filter-samples/service/samples-helper-service';
import { SampleMonitoringStorage } from '../service/sample-monitoring-storage';
import { SampleTrackingDialogService } from '../../service/dialog-service/sample-tracking-dialog-service';
import { DeleteModalData } from '../../../shared/model-dialog/delete/delete-modal.component';
import { ModalDialogService } from '../../../shared/model-dialog/modal-dialog.service';
import { concatMap } from 'rxjs/operators';
import { iif } from 'rxjs';
import { ActivityLogger } from '../../../activities/activity-logger';
import { GroupTrackingGridData } from '../../filter-samples/filter-samples-info';
import * as moment from 'moment';
import { NotificationRulesResult } from '../interface/notification-data';

@Component({
    selector: 'sm-notification-rules',
    template: require('./sm-notification-rules.component.html'),
    styles: [require('./sm-notification-rules.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SampleMonitoringNotificationRulesComponent {

    public gridDetails: GridDetails;
    public staticGridData: any;
    public dynamicGridData: any;
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public createNewRuleUrl: any;

    public dataLoaded = true;
    public showExpiredRulesToggle = false;

    constructor(
        private readonly urlBuilder: UrlBuilder,
        private breadcrumbService: BreadcrumbService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private smStorage: SampleMonitoringStorage,
        private dialogService: SampleTrackingDialogService,
        private modalDialogService: ModalDialogService,
        activityLogger: ActivityLogger
    ) {
        this.createNewRuleUrl = this.urlBuilder
            .esa()
            .sampletracking()
            .sampleMonitoring()
            .newRuleForm()
            .url();
        this.dataLoaded = false;
        let portfolioRoute: IBreadcrumb = {
            label: 'Sample Monitoring Notification Rules',
            url: this.urlBuilder.esa().sampletracking().sampleMonitoring().notificationRules().url()
        };
        this.breadcrumbService.AddRouterToBreadCrumb(portfolioRoute);
        this.loadGridData();
        activityLogger.logActivity('View Sample Monitoring Notification Rules');
    }

    public loadGridData() {
        this.smStorage.getNotificationRulesGridData().subscribe((data) => {
            if (data && data.rules && data.rules.length) {
                data.rules.forEach((rule: NotificationRulesResult) => {
                    rule.effectiveDate = rule.effectiveDate ? moment(rule.effectiveDate).format('DD-MMM-YYYY') : '';
                    rule.expirationDate = rule.expirationDate ? moment(rule.expirationDate).format('DD-MMM-YYYY') : '';

                });
                this.staticGridData = data.rules;
                this.FilterExpiredRules();
                this.gridDetails = { header: this.columnDefs(), data: this.dynamicGridData };
            }
            this.dataLoaded = true;
            this.changeDetectorRef.markForCheck();
        });
    }

    public onGridItemClick($event: any) {
        if ($event && $event.header && $event.header.actionType && $event.data && $event.data.ruleId) {
            if ($event.header.actionType === 1) { //edit
                this.urlBuilder.esa().sampletracking().sampleMonitoring().editRuleForm($event.data.ruleId).navigate();
            }

            if ($event.header.actionType === 3) { //notify
                $event.data.notificationMethod = !$event.data.notificationMethod;
                $event.header.cssClass += ' disabled';
                const notificationData = {
                    ruleId: $event.data.ruleId,
                    emailNotification: $event.data.notificationMethod ? 'T' : 'F'
                };
                this.smStorage.notificationMethod(notificationData)
                    .subscribe(() => {
                        $event.header.cssClass = $event.header.cssClass.replace(' disabled', '');
                        this.dialogService.showPostActionDialogue(`Notification ${$event.data.notificationMethod ? 'enabled' : 'disabled'} for <b>${$event.data.ruleName}</b>.`, true, '0px');
                        this.loadGridData();
                    });
            }

            if ($event.header.actionType === 5) { //delete
                const deleteDialogData = {
                    closeButtonText: 'Close',
                    deleteButtonText: 'Delete',
                    content: `Are you sure you want to delete the Rule <b>${$event.data.ruleName} ?</b>`,
                    title: 'Delete Rule'
                } as DeleteModalData;
                this.modalDialogService.showCustomDeleteDialog(deleteDialogData)
                    .pipe(
                        concatMap((dialogResult) =>
                            iif(() => dialogResult.isDeleted,
                                this.smStorage.deleteRule($event.data.ruleId)
                            )
                        )
                    )
                    .subscribe(
                        () => {
                            this.dialogService.showPostActionDialogue(`Rule <b>${$event.data.ruleName}</b> Deleted.`, true, '0px');
                            this.loadGridData();
                        },
                        () => { }
                    );
            }

        }
    }

    public showExpiredRules() {
        this.showExpiredRulesToggle = !this.showExpiredRulesToggle;
        this.FilterExpiredRules();
        this.gridDetails = { header: this.columnDefs(), data: this.dynamicGridData };
    }

    public columnDefs(): GridHeader[] {
        return [
            {
                key: 'ruleName',
                displayText: 'Rule Name',
                sort: 'asc',
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'event',
                displayText: 'Event',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'protocolCode',
                displayText: 'Protocol Number',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'effectiveDate',
                displayText: 'Notification Start Date',
                sort: 'desc',
                enableFilter: true,
                skipConversion: true,
                enableSearch: true,
                isDate: true,
            },
            {
                key: 'expirationDate',
                displayText: 'Notification End Date',
                sort: null,
                isExpirationField: true,
                skipConversion: true,
                enableFilter: true,
                enableSearch: true,
                isDate: true,
            },
            //ToDo: Scope for Manage Notfications
            {
                key: 'notificationMethod',
                displayText: 'Notification Method',
                sort: null,
                actions: [
                    {
                        cssClass: 'action slider round',
                        actionType: 3,
                        title: 'E-mail'
                    }
                ]
            },
            {
                key: 'frequency',
                displayText: 'Frequency',
                sort: null,
            },
            {
                key: 'action',
                displayText: 'Actions',
                sort: null,
                actions: [
                    {
                        cssClass: 'action edit',
                        actionType: 1,
                        title: 'Edit',
                    }, {
                        cssClass: 'action delete',
                        actionType: 5,
                        title: 'Delete'
                    }
                ]
            }
        ];
    }

    private FilterExpiredRules() {
        if (this.showExpiredRulesToggle) {
            this.dynamicGridData = this.staticGridData;
        } else {
            this.dynamicGridData = this.staticGridData.filter((x: NotificationRulesResult) => !x.isExpired);
        }
    }
}
